import React from 'react';

function TestResults({ testRun }) {
  if (!testRun) {
    return <div>No test results available</div>;
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  const formatDuration = (ms) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'passed':
        return '✅';
      case 'failed':
        return '❌';
      case 'running':
        return '⏳';
      case 'skipped':
        return '⏭️';
      default:
        return '❓';
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'passed':
        return 'status-passed';
      case 'failed':
        return 'status-failed';
      case 'running':
        return 'status-running';
      case 'skipped':
        return 'status-skipped';
      default:
        return 'status-default';
    }
  };

  // Map test types to categories
  const getCategoryForTest = (testType) => {
    if (testType.startsWith('auth_')) {
      return 'Authentication';
    } else if (testType.startsWith('group_')) {
      return 'Watchlist > Groups';
    } else if (testType.startsWith('contract_') || testType === 'ops_reset_days' || testType === 'watchlist_refresh' || testType === 'ops_bulk_refresh') {
      return 'Watchlist > Table';
    } else if (testType === 'watchlist_simulate' || testType === 'watchlist_contract' || testType === 'ops_bulk_simulation') {
      return 'Watchlist > Params Form';
    } else if (testType === 'watchlist_create' || testType.startsWith('assoc_')) {
      return 'Watchlist > Group Associations';
    } else if (testType === 'ops_group_refresh' || testType === 'ops_simulation_empty') {
      return 'Watchlist > Group Operations';
    }
    return 'Other';
  };

  // Group results by category
  const groupResultsByCategory = () => {
    if (!testRun.results) return {};

    const grouped = {};
    testRun.results.forEach(result => {
      const category = getCategoryForTest(result.test_type);
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(result);
    });

    // Sort categories in desired order
    const categoryOrder = [
      'Authentication',
      'Watchlist > Groups',
      'Watchlist > Table',
      'Watchlist > Params Form',
      'Watchlist > Group Associations',
      'Watchlist > Group Operations',
      'Other'
    ];

    const sorted = {};
    categoryOrder.forEach(cat => {
      if (grouped[cat]) {
        sorted[cat] = grouped[cat];
      }
    });

    return sorted;
  };

  const groupedResults = groupResultsByCategory();

  // Collect all failed tests for easy copying
  const failedTests = testRun.results?.filter(r => r.status === 'failed' && r.error_message) || [];

  // Collect skipped tests
  const skippedTests = testRun.results?.filter(r => r.status === 'skipped') || [];

  // Function to copy error summary to clipboard
  const copyErrorSummary = () => {
    const errorText = failedTests.map(test =>
      `❌ ${test.test_type_display || test.test_type}\n   Error: ${test.error_message}`
    ).join('\n\n');

    const fullSummary = `Test Run #${testRun.id} - ${testRun.environment}\nFailed Tests: ${testRun.failed_tests}/${testRun.total_tests}\n\n${errorText}`;

    navigator.clipboard.writeText(fullSummary).then(() => {
      alert('Error summary copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  return (
    <div className="test-results">
      <div className="results-header">
        <h2>Test Run #{testRun.id}</h2>
        <div className="run-info">
          <span className="environment-badge">{testRun.environment}</span>
          <span className={`status-badge ${getStatusClass(testRun.status)}`}>
            {testRun.status}
          </span>
        </div>
      </div>

      <div className="results-summary">
        <div className="summary-card">
          <h3>Summary</h3>
          <div className="summary-stats">
            <div className="stat">
              <label>Total Tests:</label>
              <span>{testRun.total_tests}</span>
            </div>
            <div className="stat">
              <label>Passed:</label>
              <span className="passed-count">{testRun.passed_tests}</span>
            </div>
            <div className="stat">
              <label>Failed:</label>
              <span className="failed-count">{testRun.failed_tests}</span>
            </div>
            <div className="stat">
              <label>Skipped:</label>
              <span className="skipped-count">{skippedTests.length}</span>
            </div>
            <div className="stat">
              <label>Success Rate:</label>
              <span className={testRun.success_rate === 100 ? 'success-rate' : 'partial-rate'}>
                {testRun.success_rate?.toFixed(1)}%
              </span>
            </div>
          </div>
          <div className="timing-info">
            <div className="stat">
              <label>Started:</label>
              <span>{formatDate(testRun.started_at)}</span>
            </div>
            <div className="stat">
              <label>Completed:</label>
              <span>{formatDate(testRun.completed_at)}</span>
            </div>
            <div className="stat">
              <label>Run By:</label>
              <span>{testRun.user_username}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Error Summary Section - Easy to Copy */}
      {failedTests.length > 0 && (
        <div className="error-summary-section">
          <div className="error-summary-card">
            <div className="error-summary-header">
              <h3>❌ Error Summary ({failedTests.length} Failed Tests)</h3>
              <button
                onClick={copyErrorSummary}
                className="copy-errors-btn"
                title="Copy all errors to clipboard"
              >
                📋 Copy All Errors
              </button>
            </div>
            <div className="error-summary-content">
              <div className="error-list-compact">
                {failedTests.map((test, idx) => (
                  <div key={test.id || idx} className="error-item-compact">
                    <div className="error-test-name">
                      <strong>{test.test_type_display || test.test_type}</strong>
                      <span className="error-duration">{formatDuration(test.duration_ms)}</span>
                    </div>
                    <div className="error-message-text">
                      {test.error_message}
                    </div>
                  </div>
                ))}
              </div>
              <div className="copy-hint">
                💡 Click "Copy All Errors" above to copy this entire error summary to your clipboard
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="results-details">
        <h3>Test Results by Module</h3>
        {Object.entries(groupedResults).map(([category, results]) => (
          <div key={category} className="results-category">
            <div className="category-header">
              <h4>{category}</h4>
              <span className="category-summary">
                {results.filter(r => r.status === 'passed').length}/{results.length} passed
              </span>
            </div>
            <div className="test-results-list">
              {results.map((result, index) => (
                <div key={result.id || index} className={`test-result-item ${getStatusClass(result.status)}`}>
                  <div className="result-header">
                    <span className="result-icon">{getStatusIcon(result.status)}</span>
                    <span className="result-name">{result.test_type_display || result.test_type}</span>
                    <span className="result-duration">{formatDuration(result.duration_ms)}</span>
                  </div>
                  {result.error_message && (
                    <div className="error-details">
                      <strong>Error:</strong> {result.error_message}
                    </div>
                  )}
                  {result.status === 'skipped' && result.details?.skip_reason && (
                    <div className="skip-details">
                      <strong>Skip Reason:</strong> {result.details.skip_reason}
                    </div>
                  )}
                  {result.details && Object.keys(result.details).length > 0 && result.status !== 'skipped' && (
                    <div className="test-details">
                      <details>
                        <summary>Details</summary>
                        <pre>{JSON.stringify(result.details, null, 2)}</pre>
                      </details>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {testRun.status === 'running' && (
        <div className="running-indicator">
          <div className="spinner"></div>
          <p>Tests are currently running. Results will update automatically...</p>
        </div>
      )}
    </div>
  );
}

export default TestResults;